/**
 * Sexy Hair — slider dots + page-aware prev/next controller
 *
 * Companion to Dawn's <slider-component> (defined in global.js). Dawn's
 * slider has a built-in "1 / N" numeric counter and prev/next that advance
 * by one slide at a time. This adds:
 *
 *   - dot pagination (one dot per PAGE, where a page = slidesPerView slides),
 *     auto-rebuilt on viewport resize when slidesPerView changes
 *   - page-aware prev/next: any [data-target][data-direction] button anywhere
 *     on the page advances by ONE PAGE (matching slidesPerView), not one slide
 *
 * Markup contract (per carousel):
 *
 *   <slider-component>
 *     <ul id="Slider-XYZ" class="sexyhair-foo__slider slider">
 *       <li id="Slide-XYZ-1" class="sexyhair-foo__slide slider__slide">…</li>
 *       <li id="Slide-XYZ-2" class="sexyhair-foo__slide slider__slide">…</li>
 *     </ul>
 *   </slider-component>
 *
 *   <sexyhair-slider-dots
 *     data-target="#Slider-XYZ"
 *     data-bullet-class="sexyhair-foo__bullet"
 *     data-active-class="is-active"></sexyhair-slider-dots>
 *
 *   <button type="button" data-target="#Slider-XYZ" data-direction="prev">‹</button>
 *   <button type="button" data-target="#Slider-XYZ" data-direction="next">›</button>
 *
 * The dots element sets data-locked="true" on itself when there's only one
 * page (no need to paginate). CSS can hide it via that selector.
 */
(function () {
  'use strict';

  function getSlideOffset(slides) {
    if (!slides || slides.length < 2) {
      return slides && slides[0] ? slides[0].clientWidth : 0;
    }
    return slides[1].offsetLeft - slides[0].offsetLeft;
  }

  function getSlidesPerView(slider, slides) {
    if (!slider || !slides || slides.length === 0) return 1;
    var offset = getSlideOffset(slides);
    if (offset <= 0) return 1;
    return Math.max(1, Math.round(slider.clientWidth / offset));
  }

  function getMaxScrollLeft(slider) {
    return Math.max(0, slider.scrollWidth - slider.clientWidth);
  }

  function getPageCount(slides, slidesPerView) {
    if (!slides.length || slidesPerView <= 0) return 1;
    return Math.max(1, Math.ceil(slides.length / slidesPerView));
  }

  /* Each "page" of the slider has a target scroll-left = the offsetLeft of
     its first slide, clamped to the slider's maxScrollLeft. The clamp is
     critical for the last page when slideCount is not a multiple of
     slidesPerView (e.g. 4 slides at 3-per-view). Without the clamp, the
     page-1 target sits past maxScrollLeft, so after a NEXT click we settle
     at maxScrollLeft but compute "currentPage = 0" and prev/next are stuck. */
  function getPageScrollLeft(slider, slides, slidesPerView, pageIndex) {
    var targetSlide = slides[Math.min(slides.length - 1, pageIndex * slidesPerView)];
    if (!targetSlide) return 0;
    return Math.min(targetSlide.offsetLeft, getMaxScrollLeft(slider));
  }

  function getCurrentPage(slider, slides, slidesPerView) {
    if (!slider || !slides.length || slidesPerView <= 0) return 0;
    var pageCount = getPageCount(slides, slidesPerView);
    if (pageCount < 2) return 0;

    var scrollLeft = slider.scrollLeft;
    var bestPage = 0;
    var bestDistance = Infinity;
    for (var i = 0; i < pageCount; i++) {
      var pageLeft = getPageScrollLeft(slider, slides, slidesPerView, i);
      var distance = Math.abs(scrollLeft - pageLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestPage = i;
      }
    }
    return bestPage;
  }

  function scrollToPage(slider, slides, pageIndex, slidesPerView) {
    var pageLeft = getPageScrollLeft(slider, slides, slidesPerView, pageIndex);
    slider.scrollTo({ left: pageLeft, behavior: 'smooth' });
  }

  class SexyhairSliderDots extends HTMLElement {
    connectedCallback() {
      this._build = this._build.bind(this);
      this._update = this._update.bind(this);
      this._handleResize = this._handleResize.bind(this);

      var targetSel = this.dataset.target;
      this.slider = targetSel ? document.querySelector(targetSel) : null;
      if (!this.slider) return;
      this.slides = this.slider.querySelectorAll('[id^="Slide-"]');
      if (!this.slides.length) return;

      this.bulletClass = this.dataset.bulletClass || 'sexyhair-slider-dot';
      this.activeClass = this.dataset.activeClass || 'is-active';

      requestAnimationFrame(this._build);

      this.slider.addEventListener('scroll', this._update, { passive: true });

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(this._handleResize);
        this._resizeObserver.observe(this.slider);
      } else {
        window.addEventListener('resize', this._handleResize);
      }
    }

    disconnectedCallback() {
      if (this._resizeObserver) this._resizeObserver.disconnect();
      else window.removeEventListener('resize', this._handleResize);
      if (this.slider) this.slider.removeEventListener('scroll', this._update);
    }

    _build() {
      this.slidesPerView = getSlidesPerView(this.slider, this.slides);
      this.pageCount = getPageCount(this.slides, this.slidesPerView);

      this.innerHTML = '';
      if (this.pageCount < 2) {
        this.dataset.locked = 'true';
        return;
      }
      this.dataset.locked = 'false';

      var bulletClass = this.bulletClass;
      var pageCount = this.pageCount;
      var bullets = '';
      for (var i = 0; i < pageCount; i++) {
        bullets +=
          '<button type="button" class="' +
          bulletClass +
          '" aria-label="Page ' +
          (i + 1) +
          ' of ' +
          pageCount +
          '"></button>';
      }
      this.innerHTML = bullets;

      var self = this;
      this.querySelectorAll('button').forEach(function (btn, idx) {
        btn.addEventListener('click', function () {
          scrollToPage(self.slider, self.slides, idx, self.slidesPerView);
        });
      });

      this._update();
    }

    _update() {
      if (!this.slidesPerView || this.pageCount < 2) return;
      var current = getCurrentPage(this.slider, this.slides, this.slidesPerView);
      var activeClass = this.activeClass;
      this.querySelectorAll('button').forEach(function (btn, i) {
        btn.classList.toggle(activeClass, i === current);
      });
    }

    _handleResize() {
      var prev = this.slidesPerView;
      var next = getSlidesPerView(this.slider, this.slides);
      if (next !== prev) this._build();
      else this._update();
    }
  }

  /* Guard against double-registration. Both sexyhair-pairs-well-with and
     sexyhair-brand-landing inject this script tag, and even though the
     browser usually dedupes identical script srcs, we don't rely on that —
     a duplicate customElements.define() throws DOMException and would
     prevent every slider on the page from initializing. */
  if (!customElements.get('sexyhair-slider-dots')) {
    customElements.define('sexyhair-slider-dots', SexyhairSliderDots);
  }

  /* Ditto for the document-level click delegator: registering twice would
     cause every prev/next click to advance two pages. */
  if (!window.__sexyhairSliderDotsClickBound) {
    window.__sexyhairSliderDotsClickBound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-direction][data-target]');
      if (!btn) return;
      var slider = document.querySelector(btn.dataset.target);
      if (!slider) return;
      var slides = slider.querySelectorAll('[id^="Slide-"]');
      if (!slides.length) return;
      var slidesPerView = getSlidesPerView(slider, slides);
      var pageCount = getPageCount(slides, slidesPerView);
      var currentPage = getCurrentPage(slider, slides, slidesPerView);
      var dir = btn.dataset.direction === 'prev' ? -1 : 1;
      var nextPage = Math.max(0, Math.min(pageCount - 1, currentPage + dir));
      if (nextPage === currentPage) return;
      scrollToPage(slider, slides, nextPage, slidesPerView);
    });
  }
})();
